import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Plus, Briefcase, MapPin, Copy, Trash2, Upload, Download, PenLine, StickyNote, Package, Mail, ExternalLink, Phone, Calendar } from "lucide-react";
import { toast } from "sonner";
import { apiError, fmtDate, fmtDateShort } from "@/lib/format";
import { downloadBlob } from "@/lib/download";
import { PageHeader, EmptyState } from "@/components/Page";
import AuthImage from "@/components/AuthImage";
import PhotoLightbox from "@/components/PhotoLightbox";
import SignaturePad from "@/components/SignaturePad";

const STATUS = {
  open: { label: "Offen", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  in_progress: { label: "In Arbeit", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  done: { label: "Erledigt", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const EMPTY = { title: "", description: "", client_name: "", client_phone: "", client_email: "", client_address: "", date: "", time_from: "08:00", time_to: "16:00", standard_text: "", assigned_user_ids: [] };

export default function Orders() {
  const { user, company } = useAuth();
  const isAdmin = user?.role === "admin";
  const canCreate = isAdmin || company?.employees_can_create_orders;
  const [orders, setOrders] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    const [o, e] = await Promise.all([api.get("/orders"), isAdmin ? api.get("/employees") : Promise.resolve({ data: [] })]);
    setOrders(o.data || []);
    setEmployees((e.data || []).filter((u) => u.role === "employee"));
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  const pickClient = (id) => {
    const cl = (company?.clients || []).find((c) => c.id === id);
    if (cl) setForm((f) => ({ ...f, client_name: cl.name || "", client_phone: cl.phone || "", client_email: cl.email || "", client_address: cl.address || "" }));
  };
  const pickTemplate = (id) => {
    const t = (company?.confirmation_templates || []).find((x) => x.id === id);
    if (t) setForm((f) => ({ ...f, standard_text: t.text || "" }));
  };
  const toggleEmp = (id) => setForm((f) => ({ ...f, assigned_user_ids: f.assigned_user_ids.includes(id) ? f.assigned_user_ids.filter((x) => x !== id) : [...f.assigned_user_ids, id] }));

  const create = async () => {
    if (!form.title) return toast.error("Titel erforderlich");
    try {
      await api.post("/orders", form);
      toast.success("Auftrag angelegt");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const setStatus = async (o, status) => {
    try { await api.post(`/orders/${o.id}/status`, { status }); load(); if (detail?.id === o.id) setDetail({ ...detail, status }); }
    catch (e) { toast.error(apiError(e)); }
  };
  const duplicate = async (o) => {
    try { await api.post(`/orders/${o.id}/duplicate`); toast.success("Auftrag dupliziert"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (o) => {
    try { await api.delete(`/orders/${o.id}`); toast.success("Auftrag gelöscht"); setDetail(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader
        title="Aufträge & Baustellen"
        subtitle="Verwalte Aufträge, Fotos, Material und Durchführungsnachweise."
        testid="orders-header"
        action={canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="order-open" className="brand-bg text-white hover:opacity-90"><Plus className="h-4 w-4 mr-2" /> Auftrag</Button></DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="order-dialog">
              <DialogHeader><DialogTitle>Neuer Auftrag</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2"><Label>Titel *</Label><Input data-testid="order-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
                {(company?.clients || []).length > 0 && (
                  <div className="space-y-2">
                    <Label>Kunde aus Merkliste</Label>
                    <Select onValueChange={pickClient}>
                      <SelectTrigger data-testid="order-client-pick"><SelectValue placeholder="Kunde wählen…" /></SelectTrigger>
                      <SelectContent>{(company.clients).map((c, i) => <SelectItem key={c.id || i} value={c.id || String(i)}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Auftraggeber</Label><Input data-testid="order-client-name" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Telefon</Label><Input data-testid="order-client-phone" value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} /></div>
                  <div className="space-y-2"><Label>E-Mail</Label><Input data-testid="order-client-email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Adresse</Label><Input data-testid="order-client-address" value={form.client_address} onChange={(e) => setForm({ ...form, client_address: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2"><Label>Datum</Label><Input type="date" data-testid="order-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Von</Label><Input type="time" data-testid="order-from" value={form.time_from} onChange={(e) => setForm({ ...form, time_from: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Bis</Label><Input type="time" data-testid="order-to" value={form.time_to} onChange={(e) => setForm({ ...form, time_to: e.target.value })} /></div>
                </div>
                {isAdmin && employees.length > 0 && (
                  <div className="space-y-2">
                    <Label>Zugewiesene Mitarbeiter</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {employees.map((u) => (
                        <label key={u.user_id} className="flex items-center gap-2 text-sm p-2 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50">
                          <Checkbox checked={form.assigned_user_ids.includes(u.user_id)} onCheckedChange={() => toggleEmp(u.user_id)} data-testid={`order-emp-${u.user_id}`} />
                          {u.name}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {(company?.confirmation_templates || []).length > 0 && (
                  <div className="space-y-2">
                    <Label>Standardtext-Vorlage</Label>
                    <Select onValueChange={pickTemplate}>
                      <SelectTrigger data-testid="order-template"><SelectValue placeholder="Vorlage übernehmen…" /></SelectTrigger>
                      <SelectContent>{company.confirmation_templates.map((t, i) => <SelectItem key={t.id || i} value={t.id || String(i)}>{t.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2"><Label>Standardtext / Beschreibung</Label><Textarea data-testid="order-text" value={form.standard_text} onChange={(e) => setForm({ ...form, standard_text: e.target.value })} rows={3} /></div>
              </div>
              <DialogFooter><Button onClick={create} data-testid="order-create" className="brand-bg text-white hover:opacity-90">Anlegen</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />

      {orders.length === 0 ? (
        <Card className="border-slate-200 rounded-xl"><EmptyState icon={Briefcase} title="Keine Aufträge" subtitle={canCreate ? "Lege deinen ersten Auftrag an." : "Dir wurden noch keine Aufträge zugewiesen."} testid="orders-empty" /></Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {orders.map((o) => (
            <Card key={o.id} className="p-5 border-slate-200 rounded-xl hover:shadow-sm transition-shadow cursor-pointer" onClick={() => setDetail(o)} data-testid={`order-card-${o.id}`}>
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold text-slate-900 leading-snug">{o.title}</h3>
                <Badge variant="outline" className={STATUS[o.status]?.cls}>{STATUS[o.status]?.label}</Badge>
              </div>
              {o.client_name && <div className="text-sm text-slate-600 mt-2">{o.client_name}</div>}
              {o.client_address && <div className="text-xs text-slate-400 flex items-center gap-1 mt-1"><MapPin className="h-3 w-3" /> {o.client_address}</div>}
              <div className="flex items-center gap-3 text-xs text-slate-400 mt-3">
                {o.date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {fmtDate(o.date)}</span>}
                {o.time_from && <span>{o.time_from}–{o.time_to}</span>}
              </div>
              {o.assigned_names?.length > 0 && <div className="text-xs text-slate-500 mt-2">👷 {o.assigned_names.join(", ")}</div>}
            </Card>
          ))}
        </div>
      )}

      {detail && (
        <OrderDetail
          orderId={detail.id}
          isAdmin={isAdmin}
          companyPhotosNote={company?.photos_permanent}
          onClose={() => setDetail(null)}
          onChanged={load}
          onStatus={setStatus}
          onDuplicate={duplicate}
          onDelete={del}
          statusMap={STATUS}
        />
      )}
    </div>
  );
}

function OrderDetail({ orderId, isAdmin, companyPhotosNote, onClose, onChanged, onStatus, onDuplicate, onDelete, statusMap }) {
  const [o, setO] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [note, setNote] = useState("");
  const [material, setMaterial] = useState("");
  const [signOpen, setSignOpen] = useState(false);
  const [sig, setSig] = useState([]);
  const [signName, setSignName] = useState("");
  const [inclPhotos, setInclPhotos] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(-1);

  const load = useCallback(async () => {
    const [d, p] = await Promise.all([api.get(`/orders/${orderId}`), api.get(`/orders/${orderId}/photos`)]);
    setO(d.data);
    setPhotos(p.data || []);
    setSignName(d.data?.client_name || "");
  }, [orderId]);
  useEffect(() => { load(); }, [load]);

  const addNote = async (kind, text, reset) => {
    if (!text.trim()) return;
    try { const r = await api.post(`/orders/${orderId}/notes`, { text, kind }); setO(r.data); reset(""); }
    catch (e) { toast.error(apiError(e)); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/orders/${orderId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto hochgeladen");
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const delPhoto = async (pid) => {
    try { await api.delete(`/order-photos/${pid}`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const zip = async () => {
    try { await downloadBlob(`/orders/${orderId}/photos/zip`, `fotos_${o.title}.zip`); }
    catch (e) { toast.error("Keine Fotos zum Herunterladen"); }
  };

  const doSign = async () => {
    if (!signName.trim()) return toast.error("Name erforderlich");
    if (sig.length === 0) return toast.error("Bitte unterschreiben");
    try {
      const r = await api.post(`/orders/${orderId}/sign`, { signed_by_name: signName, signature_paths: sig, include_photos: inclPhotos });
      if (r.data.emailed) toast.success("Nachweis unterschrieben & E-Mail versendet");
      else toast.success("Nachweis unterschrieben" + (r.data.email_error ? " (E-Mail fehlgeschlagen)" : " (kein Empfänger)"));
      setSignOpen(false);
      setSig([]);
      setO(r.data);
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };

  const notes = (o?.notes || []).filter((n) => n.kind === "note");
  const materials = (o?.notes || []).filter((n) => n.kind === "material");
  const mapUrl = o?.client_address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(o.client_address)}` : null;

  return (
    <Sheet open onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="order-detail">
        {!o ? <div className="p-8 text-slate-400">Wird geladen…</div> : (
          <>
            <SheetHeader>
              <div className="flex items-center justify-between gap-2 pr-6">
                <SheetTitle className="text-left">{o.title}</SheetTitle>
                <Badge variant="outline" className={statusMap[o.status]?.cls}>{statusMap[o.status]?.label}</Badge>
              </div>
            </SheetHeader>

            <div className="space-y-6 mt-4">
              {/* status control */}
              <div className="flex items-center gap-2">
                {["open", "in_progress", "done"].map((s) => (
                  <Button key={s} size="sm" variant={o.status === s ? "default" : "outline"} className={o.status === s ? "brand-bg text-white" : ""} onClick={() => onStatus(o, s)} data-testid={`order-status-${s}`}>
                    {statusMap[s].label}
                  </Button>
                ))}
              </div>

              {/* client info */}
              <Card className="p-4 border-slate-200 rounded-xl text-sm space-y-1.5">
                {o.client_name && <div className="font-medium text-slate-900">{o.client_name}</div>}
                {o.client_phone && <a href={`tel:${o.client_phone}`} className="flex items-center gap-2 text-slate-600"><Phone className="h-3.5 w-3.5" /> {o.client_phone}</a>}
                {o.client_email && <a href={`mailto:${o.client_email}`} className="flex items-center gap-2 text-slate-600"><Mail className="h-3.5 w-3.5" /> {o.client_email}</a>}
                {o.client_address && <a href={mapUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 brand-text" data-testid="order-map-link"><MapPin className="h-3.5 w-3.5" /> {o.client_address} <ExternalLink className="h-3 w-3" /></a>}
                {o.date && <div className="text-slate-500 pt-1">{fmtDate(o.date)} · {o.time_from}–{o.time_to}</div>}
                {o.assigned_names?.length > 0 && <div className="text-slate-500">Team: {o.assigned_names.join(", ")}</div>}
                {o.standard_text && <div className="text-slate-600 whitespace-pre-line pt-2 border-t border-slate-100 mt-2">{o.standard_text}</div>}
              </Card>

              {/* photos */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-slate-900 text-sm">Fotos</h4>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={zip} data-testid="order-zip"><Download className="h-4 w-4 mr-1" /> ZIP</Button>
                    <label className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-md border border-slate-200 cursor-pointer hover:bg-slate-50" data-testid="order-photo-upload-label">
                      <Upload className="h-4 w-4" /> {uploading ? "…" : "Hochladen"}
                      <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="order-photo-input" />
                    </label>
                  </div>
                </div>
                <div className="text-xs text-slate-400 mb-2">{companyPhotosNote ? "Fotos werden dauerhaft gespeichert." : "Fotos werden nach 7 Tagen automatisch gelöscht."}</div>
                {photos.length === 0 ? (
                  <div className="text-sm text-slate-400 py-4 text-center border border-dashed border-slate-200 rounded-lg">Noch keine Fotos</div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {photos.map((p, idx) => (
                      <div key={p.id} className="relative group" data-testid={`order-photo-${p.id}`}>
                        <button type="button" onClick={() => setLightbox(idx)} className="block w-full" data-testid={`order-photo-open-${p.id}`}>
                          <AuthImage path={`/order-photos/${p.id}/download`} alt={p.filename} className="h-24 w-full object-cover rounded-lg border border-slate-200 cursor-pointer hover:opacity-90 transition-opacity" />
                        </button>
                        <button onClick={() => delPhoto(p.id)} className="absolute top-1 right-1 bg-white/90 rounded-full p-1 text-rose-500 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`order-photo-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <PhotoLightbox photos={photos} index={lightbox} onClose={() => setLightbox(-1)} onIndex={setLightbox} />

              {/* notes & material */}
              <div className="grid gap-4">
                <div>
                  <h4 className="font-semibold text-slate-900 text-sm flex items-center gap-2 mb-2"><StickyNote className="h-4 w-4" /> Notizen</h4>
                  <div className="space-y-1.5 mb-2">
                    {notes.map((n) => <div key={n.id} className="text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2"><span className="text-slate-700">{n.text}</span><span className="text-xs text-slate-400 ml-2">{n.user_name}</span></div>)}
                  </div>
                  <div className="flex gap-2">
                    <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Notiz…" data-testid="order-note-input" onKeyDown={(e) => e.key === "Enter" && addNote("note", note, setNote)} />
                    <Button size="sm" onClick={() => addNote("note", note, setNote)} data-testid="order-note-add" className="brand-bg text-white">+</Button>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 text-sm flex items-center gap-2 mb-2"><Package className="h-4 w-4" /> Material</h4>
                  <div className="space-y-1.5 mb-2">
                    {materials.map((n) => <div key={n.id} className="text-sm bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"><span className="text-slate-700">{n.text}</span><span className="text-xs text-slate-400 ml-2">{n.user_name}</span></div>)}
                  </div>
                  <div className="flex gap-2">
                    <Input value={material} onChange={(e) => setMaterial(e.target.value)} placeholder="Material…" data-testid="order-material-input" onKeyDown={(e) => e.key === "Enter" && addNote("material", material, setMaterial)} />
                    <Button size="sm" onClick={() => addNote("material", material, setMaterial)} data-testid="order-material-add" className="brand-bg text-white">+</Button>
                  </div>
                </div>
              </div>

              {/* signed info */}
              {o.signed_at && (
                <Card className="p-4 border-emerald-200 bg-emerald-50 rounded-xl text-sm" data-testid="order-signed-info">
                  <div className="font-medium text-emerald-800">Unterschrieben von {o.signed_by_name}</div>
                  <div className="text-emerald-700 text-xs">{fmtDate(o.signed_at)}{o.confirmation_sent_at ? " · E-Mail versendet" : ""}</div>
                  {o.share_token && <a href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${o.id}/confirmation?t=${o.share_token}`} target="_blank" rel="noreferrer" className="brand-text text-xs flex items-center gap-1 mt-1">Nachweis öffnen <ExternalLink className="h-3 w-3" /></a>}
                </Card>
              )}

              {/* actions */}
              <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
                <Dialog open={signOpen} onOpenChange={setSignOpen}>
                  <DialogTrigger asChild><Button className="brand-bg text-white hover:opacity-90" data-testid="order-sign-open"><PenLine className="h-4 w-4 mr-2" /> Nachweis unterschreiben</Button></DialogTrigger>
                  <DialogContent data-testid="order-sign-dialog">
                    <DialogHeader><DialogTitle>Durchführungsnachweis</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                      <div className="space-y-2"><Label>Name Auftraggeber</Label><Input value={signName} onChange={(e) => setSignName(e.target.value)} data-testid="sign-name" /></div>
                      <div className="space-y-2"><Label>Unterschrift</Label><SignaturePad onChange={setSig} /></div>
                      <label className="flex items-center gap-2 text-sm"><Checkbox checked={inclPhotos} onCheckedChange={(v) => setInclPhotos(!!v)} data-testid="sign-include-photos" /> Fotos in den Nachweis aufnehmen</label>
                    </div>
                    <DialogFooter><Button onClick={doSign} data-testid="sign-submit" className="brand-bg text-white hover:opacity-90">Unterschreiben & senden</Button></DialogFooter>
                  </DialogContent>
                </Dialog>
                <Button variant="outline" onClick={() => onDuplicate(o)} data-testid="order-duplicate"><Copy className="h-4 w-4 mr-2" /> Duplizieren</Button>
                {isAdmin && <Button variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => onDelete(o)} data-testid="order-delete"><Trash2 className="h-4 w-4 mr-2" /> Löschen</Button>}
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
